// src/pages/TermsPage.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, ShoppingCart, Shield, AlertTriangle, Scale } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useSearch } from '../context/SearchContext';
import { openCartSidebar } from '../components/CartSidebar';

export default function TermsPage() {
  const navigate = useNavigate();
  const { cartCount } = useCart();
  const { wishlistCount } = useWishlist();
  const { searchQuery, setSearchQuery } = useSearch();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div className="terms-page">
      <Style />
      
      <Header
        wishlistCount={wishlistCount}
        cartCount={cartCount}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
        onCartClick={openCartSidebar}
        onWishlistClick={() => navigate('/wishlist')}
        onCampaignsClick={() => navigate('/campaigns')}
      />

      <div className="page-hero">
        <div className="container">
          <FileText size={48} className="hero-icon" />
          <h1>Kullanım Koşulları</h1>
          <p>Web sitemizi kullanarak kabul ettiğiniz kurallar ve şartlar</p>
          <div className="update-date">Son Güncelleme: 15 Aralık 2024</div>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          <div className="content-layout">
            {/* Sidebar */}
            <aside className="sidebar">
              <nav className="sticky-nav">
                <h3>İçindekiler</h3>
                <ul>
                  <li><a href="#introduction">Giriş</a></li>
                  <li><a href="#definitions">Tanımlar</a></li>
                  <li><a href="#acceptance">Kabul ve Onay</a></li>
                  <li><a href="#registration">Üyelik</a></li>
                  <li><a href="#orders">Sipariş ve Satış</a></li>
                  <li><a href="#prices">Fiyat ve Ödeme</a></li>
                  <li><a href="#shipping">Teslimat</a></li>
                  <li><a href="#cancellation">İptal ve İade</a></li>
                  <li><a href="#intellectual">Fikri Mülkiyet</a></li>
                  <li><a href="#prohibited">Yasak Faaliyetler</a></li>
                  <li><a href="#liability">Sorumluluk</a></li>
                  <li><a href="#termination">Fesih</a></li>
                  <li><a href="#disputes">Uyuşmazlık</a></li>
                  <li><a href="#changes">Değişiklikler</a></li>
                  <li><a href="#contact">İletişim</a></li>
                </ul>
              </nav>
            </aside>

            {/* Main Content */}
            <main className="main-content">
              <section id="introduction" className="content-section">
                <h2>1. Giriş</h2>
                <div className="content-box">
                  <p>
                    Bu Kullanım Koşulları ("Koşullar"), Tronapazarlama tarafından işletilen 
                    www.tronapazarlama.com web sitesi ("Site") ve ilgili tüm hizmetlerin 
                    kullanımını düzenler.
                  </p>
                  <p>
                    Sitemizi ziyaret ederek veya hizmetlerimizi kullanarak, bu Koşulları okuduğunuzu, 
                    anladığınızı ve bunlara bağlı kalmayı kabul ettiğinizi beyan edersiniz.
                  </p>
                  <div className="important-note">
                    <AlertTriangle size={20} />
                    <p>
                      <strong>Önemli:</strong> Bu koşulları kabul etmiyorsanız, lütfen sitemizi 
                      kullanmayın.
                    </p>
                  </div>
                </div>
              </section>

              <section id="definitions" className="content-section">
                <h2>2. Tanımlar</h2>
                <div className="content-box">
                  <div className="definition-list">
                    <div className="definition-item">
                      <h4>"Site"</h4>
                      <p>www.tronapazarlama.com alan adı ve tüm alt sayfaları</p>
                    </div>
                    <div className="definition-item">
                      <h4>"Kullanıcı"</h4>
                      <p>Siteyi ziyaret eden veya kullanan herhangi bir kişi</p>
                    </div>
                    <div className="definition-item">
                      <h4>"Üye"</h4>
                      <p>Siteye kayıt olmuş ve hesap oluşturmuş kullanıcı</p>
                    </div>
                    <div className="definition-item">
                      <h4>"Ürün"</h4>
                      <p>Site üzerinden satışa sunulan mal veya hizmetler</p>
                    </div>
                    <div className="definition-item">
                      <h4>"Sipariş"</h4>
                      <p>Kullanıcının ürün satın alma talebi</p>
                    </div>
                    <div className="definition-item">
                      <h4>"Taraflar"</h4>
                      <p>Tronapazarlama ve Kullanıcı/Üye</p>
                    </div>
                  </div>
                </div>
              </section>

              <section id="acceptance" className="content-section">
                <h2>3. Kabul ve Onay</h2>
                <div className="content-box">
                  <p>Bu Koşulları kabul ederek:</p>
                  <ul className="check-list">
                    <li>18 yaşından büyük olduğunuzu veya yasal vasi gözetiminde olduğunuzu</li>
                    <li>Yasal olarak bağlayıcı sözleşmeler yapma ehliyetine sahip olduğunuzu</li>
                    <li>Verdiğiniz bilgilerin doğru ve güncel olduğunu</li>
                    <li>Tüm yürürlükteki yasalara uyacağınızı</li>
                    <li>Ticari amaçlarla yasal izinlere sahip olduğunuzu</li>
                  </ul>
                  <p>
                    Bu koşulları kabul etmeyenler sitemizi kullanamaz ve sipariş veremez.
                  </p>
                </div>
              </section>

              <section id="registration" className="content-section">
                <h2>4. Üyelik ve Hesap Güvenliği</h2>
                <div className="content-box">
                  <h3>4.1. Hesap Oluşturma</h3>
                  <p>
                    Üye olmak için geçerli bir e-posta adresi ve şifre belirlemeniz gerekmektedir. 
                    Verdiğiniz tüm bilgilerin doğru, güncel ve eksiksiz olması zorunludur.
                  </p>

                  <h3>4.2. Hesap Güvenliği</h3>
                  <ul className="info-list">
                    <li>Şifrenizi gizli tutmaktan siz sorumlusunuz</li>
                    <li>Hesabınızda gerçekleşen tüm işlemlerden siz sorumlusunuz</li>
                    <li>Yetkisiz erişim durumunda derhal bizi bilgilendirmelisiniz</li>
                    <li>Hesabınızı başkalarıyla paylaşamazsınız</li>
                  </ul>

                  <h3>4.3. Hesap Askıya Alma/Kapatma</h3>
                  <p>
                    Aşağıdaki durumlarda hesabınızı askıya alabilir veya kapatma hakkını saklı tutarız:
                  </p>
                  <ul className="warning-list">
                    <li>Koşulları ihlal etmeniz</li>
                    <li>Sahte veya yanıltıcı bilgi vermeniz</li>
                    <li>Ödeme sorunları yaşanması</li>
                    <li>Yasa dışı faaliyetlerde bulunmanız</li>
                  </ul>
                </div>
              </section>

              <section id="orders" className="content-section">
                <h2>5. Sipariş ve Satış Süreci</h2>
                <div className="content-box">
                  <h3>5.1. Sipariş Verme</h3>
                  <p>
                    Sipariş vermek, ürünü satın almak için yasal olarak bağlayıcı bir teklif oluşturur. 
                    Siparişinizi göndermeden önce tüm detayları kontrol etmekten siz sorumlusunuz.
                  </p>

                  <h3>5.2. Sipariş Onayı</h3>
                  <p>
                    Siparişiniz bize ulaştıktan sonra bir onay e-postası alacaksınız. Bu e-posta, 
                    siparişinizin alındığını gösterir ancak kabul edildiği anlamına gelmez.
                  </p>

                  <h3>5.3. Sipariş Reddi</h3>
                  <p>
                    Aşağıdaki durumlarda siparişinizi reddetme hakkımız saklıdır:
                  </p>
                  <ul className="info-list">
                    <li>Ürün stokta mevcut değilse</li>
                    <li>Fiyatlandırma hatası varsa</li>
                    <li>Ödeme sorunları yaşanırsa</li>
                    <li>Teslimat adresi hatalı veya eksikse</li>
                    <li>Teknik veya sistem hatası oluşursa</li>
                  </ul>

                  <h3>5.4. Stok Durumu</h3>
                  <p>
                    Tüm ürünler stok durumuna tabidir. Sipariş sonrası stok tükenmesi durumunda, 
                    sizi bilgilendirerek siparişinizi iptal edebilir veya alternatif önerebiliriz.
                  </p>
                </div>
              </section>

              <section id="prices" className="content-section">
                <h2>6. Fiyatlandırma ve Ödeme</h2>
                <div className="content-box">
                  <h3>6.1. Fiyatlar</h3>
                  <p>
                    Site üzerinde görüntülenen tüm fiyatlar Türk Lirası (₺) cinsindendir ve KDV dahildir. 
                    Fiyatlar önceden haber verilmeksizin değiştirilebilir.
                  </p>

                  <h3>6.2. Fiyatlandırma Hataları</h3>
                  <p>
                    Açık fiyatlandırma hataları durumunda (örneğin 10,000₺ yerine 10₺), 
                    siparişi iptal etme veya doğru fiyatla devam etme seçeneği sunma hakkımız vardır.
                  </p>

                  <h3>6.3. Ödeme Yöntemleri</h3>
                  <ul className="info-list">
                    <li>Kredi kartı (Visa, Mastercard, American Express)</li>
                    <li>Banka kartı (debit kart)</li>
                    <li>Tüm ödemeler 3D Secure ile güvence altındadır</li>
                  </ul>

                  <h3>6.4. Ödeme Güvenliği</h3>
                  <p>
                    Kredi kartı bilgileriniz asla sistemimizde saklanmaz. Tüm ödemeler SSL 
                    şifreleme ile korunur ve PCI-DSS standartlarına uygundur.
                  </p>
                </div>
              </section>

              <section id="shipping" className="content-section">
                <h2>7. Kargo ve Teslimat</h2>
                <div className="content-box">
                  <h3>7.1. Teslimat Süreleri</h3>
                  <p>
                    Standart teslimat 2-6 iş günü içindedir. Süre bölgenize ve ürüne göre değişebilir. 
                    Belirtilen süreler tahminidir ve garanti değildir.
                  </p>

                  <h3>7.2. Kargo Ücreti</h3>
                  <p>
                    Kargo ücreti 
                    ürüne göre farklılık göstermektedir.
                  </p>

                  <h3>7.3. Teslimat Adresi</h3>
                  <p>
                    Doğru ve eksiksiz adres bilgisi vermekten siz sorumlusunuz. Hatalı adres nedeniyle 
                    oluşan gecikmelerden sorumlu değiliz.
                  </p>

                  <h3>7.4. Teslim Alma</h3>
                  <p>
                    Ürünü teslim alırken kimlik ibraz etmeniz ve paketi kontrol etmeniz önerilir. 
                    Hasarlı paketleri kabul etmeden önce tutanak tutulmasını talep edebilirsiniz.
                  </p>
                </div>
              </section>

              <section id="cancellation" className="content-section">
                <h2>8. İptal ve İade Hakkı</h2>
                <div className="content-box">
                  <h3>8.1. Cayma Hakkı (7 Gün)</h3>
                  <p>
                    Mesafeli Satış Sözleşmeleri Yönetmeliği uyarınca, ürünü teslim aldıktan sonra 
                    7 gün içinde herhangi bir gerekçe göstermeksizin ve cezai şart ödemeksizin 
                    sözleşmeden cayma hakkınız vardır.
                  </p>

                  <h3>8.2. İade Koşulları</h3>
                  <p>İade edilecek ürünler:</p>
                  <ul className="check-list">
                    <li>Kullanılmamış ve hasarsız olmalıdır</li>
                    <li>Orijinal ambalajında ve etiketiyle birlikte olmalıdır</li>
                    <li>Hijyen bandı açılmamış olmalıdır</li>
                    <li>Fatura ile birlikte iade edilmelidir</li>
                  </ul>

                  <h3>8.3. İade Edilemeyen Ürünler</h3>
                  <ul className="warning-list">
                    <li>Hijyen bandı açılmış kişisel bakım ürünleri</li>
                    <li>Açılmış kozmetik ürünler</li>
                    <li>İç giyim ürünleri</li>
                    <li>Gıda ve içecek ürünleri</li>
                    <li>Dijital içerik ve yazılımlar</li>
                    <li>Özel üretim ürünler</li>
                  </ul>

                  <h3>8.4. İade Süreci</h3>
                  <p>
                    İade talebiniz onaylandıktan sonra, ürün depomıza ulaştığında incelenir. 
                    Onay durumunda, ödeme iadeniz 5-10 iş günü içinde hesabınıza yansır.
                  </p>
                </div>
              </section>

              <section id="intellectual" className="content-section">
                <h2>9. Fikri Mülkiyet Hakları</h2>
                <div className="content-box">
                  <p>
                    Site üzerindeki tüm içerik, tasarım, logo, metin, grafik, yazılım ve diğer 
                    materyaller Tronapazarlama'nın mülkiyetindedir ve telif hakkı, marka ve diğer 
                    fikri mülkiyet yasalarıyla korunmaktadır.
                  </p>
                  <div className="highlight-box">
                    <Scale size={24} />
                    <div>
                      <h4>Yasak Kullanımlar</h4>
                      <ul>
                        <li>İçeriği kopyalamak, çoğaltmak veya dağıtmak</li>
                        <li>Ticari amaçlarla kullanmak</li>
                        <li>Ürün görsellerini izinsiz kullanmak</li>
                        <li>Site içeriğini başka platformlarda yayınlamak</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              <section id="prohibited" className="content-section">
                <h2>10. Yasak Faaliyetler</h2>
                <div className="content-box">
                  <p>Sitemizi kullanırken aşağıdaki faaliyetlerde bulunamazsınız:</p>
                  <div className="prohibited-grid">
                    <div className="prohibited-item">
                      <h4>❌ Yasal İhlaller</h4>
                      <p>Yürürlükteki yasalara aykırı faaliyetler</p>
                    </div>
                    <div className="prohibited-item">
                      <h4>❌ Güvenlik İhlalleri</h4>
                      <p>Sisteme yetkisiz erişim veya zarar verme</p>
                    </div>
                    <div className="prohibited-item">
                      <h4>❌ Dolandırıcılık</h4>
                      <p>Sahte bilgi, hileli ödeme, kimlik hırsızlığı</p>
                    </div>
                    <div className="prohibited-item">
                      <h4>❌ Spam ve Taciz</h4>
                      <p>İstenmeyen mesajlar, rahatsız edici içerik</p>
                    </div>
                    <div className="prohibited-item">
                      <h4>❌ Ticari Kötüye Kullanım</h4>
                      <p>Yeniden satış, stok biriktirme, arbitraj</p>
                    </div>
                    <div className="prohibited-item">
                      <h4>❌ Bot/Otomasyon</h4>
                      <p>Otomatik araçlarla site kullanımı</p>
                    </div>
                  </div>
                </div>
              </section>

              <section id="liability" className="content-section">
                <h2>11. Sorumluluk Sınırlaması</h2>
                <div className="content-box">
                  <p>
                    Tronapazarlama, yasaların izin verdiği ölçüde, aşağıdaki durumlardan 
                    sorumlu değildir:
                  </p>
                  <ul className="info-list">
                    <li>Üçüncü taraf hizmetlerden kaynaklanan sorunlar (kargo, ödeme sağlayıcıları)</li>
                    <li>Kullanıcı hatalarından kaynaklanan kayıplar</li>
                    <li>Kesinti, gecikme veya teknik arızalar</li>
                    <li>Virüs veya zararlı yazılımlardan kaynaklanan zararlar</li>
                    <li>Force majeure (doğal afet, savaş, vs.) durumları</li>
                  </ul>
                  <p>
                    Sorumluluğumuz, ödediğiniz tutarla sınırlıdır. Dolaylı, arızi veya 
                    sonuç olarak ortaya çıkan zararlardan sorumlu değiliz.
                  </p>
                </div>
              </section>

              <section id="termination" className="content-section">
                <h2>12. Sözleşmenin Feshi</h2>
                <div className="content-box">
                  <h3>12.1. Kullanıcı Tarafından Fesih</h3>
                  <p>
                    Hesabınızı istediğiniz zaman "Hesap Ayarları" bölümünden kapatabilirsiniz. 
                    Hesap kapatma işlemi geri alınamaz.
                  </p>

                  <h3>12.2. Tronapazarlama Tarafından Fesih</h3>
                  <p>
                    Aşağıdaki durumlarda hesabınızı askıya alabilir veya kapatma hakkımız vardır:
                  </p>
                  <ul className="warning-list">
                    <li>Bu Koşulları ihlal etmeniz</li>
                    <li>Ödeme yükümlülüklerini yerine getirmemeniz</li>
                    <li>Yasadışı faaliyetlerde bulunmanız</li>
                    <li>Sahte veya yanıltıcı bilgi vermeniz</li>
                  </ul>

                  <h3>12.3. Fesih Sonuçları</h3>
                  <p>
                    Hesap kapatıldığında tüm verileriniz silinir ve erişiminiz engellenir. 
                    Ödenmemiş borçlarınız devam eder.
                  </p>
                </div>
              </section>

              <section id="disputes" className="content-section">
                <h2>13. Uyuşmazlıkların Çözümü</h2>
                <div className="content-box">
                  <h3>13.1. Uygulanacak Hukuk</h3>
                  <p>
                    Bu Koşullar, Türkiye Cumhuriyeti yasalarına tabidir ve bu yasalara göre yorumlanır.
                  </p>

                  <h3>13.2. Yetkili Mahkemeler</h3>
                  <p>
                    Bu Koşullardan doğan uyuşmazlıklarda, Antalya Mahkemeleri ve İcra Daireleri 
                    yetkilidir.
                  </p>

                  <h3>13.3. Tüketici Hakları</h3>
                  <p>
                    Tüketici sıfatıyla alışveriş yapan kullanıcılar için, Tüketici Hakem Heyetleri 
                    ve Tüketici Mahkemeleri de yetkilidir. İlgili parasal limitler:
                  </p>
                  <ul className="info-list">
                    <li>İlçe Tüketici Hakem Heyeti: 0 - 37.500₺</li>
                    <li>İl Tüketici Hakem Heyeti: 37.500₺ - 50.000₺</li>
                    <li>Tüketici Mahkemesi: 50.000₺ üzeri</li>
                  </ul>
                </div>
              </section>

              <section id="changes" className="content-section">
                <h2>14. Koşullarda Değişiklik</h2>
                <div className="content-box">
                  <p>
                    Tronapazarlama, bu Kullanım Koşulları'nı herhangi bir zamanda güncelleme 
                    hakkını saklı tutar. Önemli değişiklikler yapıldığında, sizi e-posta veya 
                    site bildirimi ile bilgilendireceğiz.
                  </p>
                  <p>
                    Değişiklikler yayınlandıktan sonra siteyi kullanmaya devam etmeniz, 
                    yeni koşulları kabul ettiğiniz anlamına gelir.
                  </p>
                  <div className="important-note">
                    <AlertTriangle size={20} />
                    <p>
                      Son güncelleme tarihini sayfanın başında bulabilirsiniz. Bu sayfayı 
                      düzenli olarak kontrol etmenizi öneririz.
                    </p>
                  </div>
                </div>
              </section>

              <section id="contact" className="content-section">
                <h2>15. İletişim Bilgileri</h2>
                <div className="content-box">
                  <p>
                    Bu Kullanım Koşulları hakkında sorularınız veya endişeleriniz varsa, 
                    bizimle iletişime geçebilirsiniz:
                  </p>
                  <div className="contact-grid">
                    <div className="contact-card">
                      <h4>📧 E-posta</h4>
                      <a href="mailto:info@tronapazarlama.com">info@tronapazarlama.com</a>
                    </div>
                    <div className="contact-card">
                      <h4>📞 Telefon</h4>
                      <a href="tel:08501234567">0850 123 45 67</a>
                    </div>
                    <div className="contact-card">
                      <h4>📍 Adres</h4>
                      <p>Antalya, Türkiye</p>
                    </div>
                    <div className="contact-card">
                      <h4>⏰ Çalışma Saatleri</h4>
                      <p>Hafta içi 09:00 - 18:00</p>
                    </div>
                  </div>
                </div>
              </section>

              <div className="final-note">
                <Shield size={32} />
                <div>
                  <h3>Yasal Uyarı</h3>
                  <p>
                    Bu belge yasal bir sözleşme niteliğindedir. Sitemizi kullanarak bu koşulları 
                    kabul etmiş sayılırsınız. Anlamadığınız veya katılmadığınız herhangi bir 
                    madde varsa, lütfen kullanmadan önce bizimle iletişime geçin.
                  </p>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function Style() {
  return (
    <style>{`
      * { margin: 0; padding: 0; box-sizing: border-box; }
      
      .terms-page {
        min-height: 100vh;
        background: #f8f9fa;
      }

      .container {
        max-width: 1320px;
        margin: 0 auto;
        padding: 0 20px;
      }

      .page-hero {
        background: linear-gradient(135deg, #2B2B2B 0%, #1a1a1a 100%);
        padding: 80px 0;
        text-align: center;
        color: #fff;
      }

      .hero-icon {
        margin-bottom: 24px;
        opacity: 0.9;
      }

      .page-hero h1 {
        font-size: 48px;
        font-weight: 800;
        margin-bottom: 16px;
      }

      .page-hero p {
        font-size: 18px;
        opacity: 0.8;
        margin-bottom: 16px;
      }

      .update-date {
        display: inline-block;
        background: rgba(255,255,255,0.2);
        padding: 8px 16px;
        border-radius: 8px;
        font-size: 14px;
      }

      .page-content {
        padding: 60px 0;
      }

      .content-layout {
        display: grid;
        grid-template-columns: 280px 1fr;
        gap: 40px;
        align-items: start;
      }

      /* Sidebar */
      .sidebar {
        position: relative;
      }

      .sticky-nav {
        position: sticky;
        top: 100px;
        background: #fff;
        padding: 24px;
        border-radius: 16px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.04);
      }

      .sticky-nav h3 {
        font-size: 18px;
        margin-bottom: 16px;
        color: #2B2B2B;
      }

      .sticky-nav ul {
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .sticky-nav a {
        display: block;
        padding: 10px 12px;
        color: #495057;
        text-decoration: none;
        border-radius: 8px;
        transition: all 0.2s;
        font-size: 14px;
      }

      .sticky-nav a:hover {
        background: #f8f9fa;
        color: #E5332A;
        transform: translateX(4px);
      }

      /* Main Content */
      .main-content {
        min-width: 0;
      }

      .content-section {
        margin-bottom: 48px;
      }

      .content-section h2 {
        font-size: 32px;
        font-weight: 800;
        margin-bottom: 24px;
        color: #2B2B2B;
        padding-top: 80px;
        margin-top: -80px;
      }

      .content-section h3 {
        font-size: 24px;
        font-weight: 700;
        margin: 32px 0 16px;
        color: #2B2B2B;
      }

      .content-section h4 {
        font-size: 18px;
        font-weight: 600;
        margin-bottom: 12px;
        color: #2B2B2B;
      }

      .content-box {
        background: #fff;
        padding: 32px;
        border-radius: 16px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.04);
      }

      .content-box p {
        color: #495057;
        line-height: 1.8;
        margin-bottom: 16px;
      }

      .content-box a {
        color: #E5332A;
        text-decoration: none;
        font-weight: 600;
      }

      .content-box a:hover {
        text-decoration: underline;
      }

      /* Lists */
      .info-list, .check-list, .warning-list {
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin: 16px 0;
      }

      .info-list li, .check-list li, .warning-list li {
        padding-left: 28px;
        position: relative;
        color: #495057;
        line-height: 1.7;
      }

      .info-list li::before {
        content: '•';
        position: absolute;
        left: 0;
        color: #E5332A;
        font-weight: 700;
        font-size: 20px;
      }

      .check-list li::before {
        content: '✓';
        position: absolute;
        left: 0;
        color: #06d6a0;
        font-weight: 700;
        font-size: 18px;
      }

      .warning-list li::before {
        content: '⚠';
        position: absolute;
        left: 0;
        font-size: 16px;
      }

      /* Definition List */
      .definition-list {
        display: grid;
        gap: 20px;
        margin: 24px 0;
      }

      .definition-item {
        padding: 20px;
        background: #f8f9fa;
        border-left: 4px solid #E5332A;
        border-radius: 8px;
      }

      .definition-item h4 {
        color: #E5332A;
        margin-bottom: 8px;
      }

      .definition-item p {
        margin: 0;
        font-size: 14px;
      }

      /* Important Note */
      .important-note {
        display: flex;
        gap: 12px;
        padding: 20px;
        background: #fff5f5;
        border: 2px solid #E5332A;
        border-radius: 12px;
        margin: 24px 0;
      }

      .important-note svg {
        color: #E5332A;
        flex-shrink: 0;
      }

      .important-note p {
        margin: 0;
      }

      /* Highlight Box */
      .highlight-box {
        display: flex;
        gap: 16px;
        padding: 24px;
        background: #eff6ff;
        border-left: 4px solid #3b82f6;
        border-radius: 8px;
        margin: 24px 0;
      }

      .highlight-box svg {
        color: #3b82f6;
        flex-shrink: 0;
      }

      .highlight-box h4 {
        margin-bottom: 12px;
      }

      .highlight-box ul {
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .highlight-box li {
        color: #495057;
        padding-left: 20px;
        position: relative;
        font-size: 14px;
      }

      .highlight-box li::before {
        content: '•';
        position: absolute;
        left: 0;
        color: #3b82f6;
        font-weight: 700;
      }

      /* Prohibited Grid */
      .prohibited-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;
        margin: 24px 0;
      }

      .prohibited-item {
        padding: 20px;
        background: #fef2f2;
        border: 2px solid #ef4444;
        border-radius: 12px;
      }

      .prohibited-item h4 {
        color: #ef4444;
        margin-bottom: 8px;
      }

      .prohibited-item p {
        margin: 0;
        font-size: 14px;
      }

      /* Contact Grid */
      .contact-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;
        margin: 24px 0;
      }

      .contact-card {
        padding: 20px;
        background: #f8f9fa;
        border-radius: 12px;
      }

      .contact-card h4 {
        margin-bottom: 12px;
        color: #2B2B2B;
      }

      .contact-card a {
        color: #E5332A;
        font-weight: 600;
      }

      .contact-card p {
        margin: 0;
        color: #495057;
      }

      /* Final Note */
      .final-note {
        display: flex;
        gap: 20px;
        padding: 32px;
        background: linear-gradient(135deg, #fffbeb, #fef3c7);
        border: 2px solid #f59e0b;
        border-radius: 16px;
        margin-top: 48px;
      }

      .final-note svg {
        color: #f59e0b;
        flex-shrink: 0;
      }

      .final-note h3 {
        font-size: 24px;
        margin-bottom: 12px;
        color: #2B2B2B;
      }

      .final-note p {
        margin: 0;
        color: #495057;
        line-height: 1.7;
      }

      /* Responsive */
      @media (max-width: 992px) {
        .content-layout {
          grid-template-columns: 1fr;
        }

        .sidebar {
          order: 2;
        }

        .sticky-nav {
          position: static;
        }

        .prohibited-grid {
          grid-template-columns: 1fr;
        }

        .contact-grid {
          grid-template-columns: 1fr;
        }
      }

      @media (max-width: 768px) {
        .page-hero h1 {
          font-size: 32px;
        }

        .content-section h2 {
          font-size: 24px;
        }

        .content-box {
          padding: 24px;
        }
      }
    `}</style>
  );
}